'use client';

import { useEffect, useRef } from 'react';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import { identify, reset } from './client';
import { isPostHogEnabled } from './config';

/**
 * Ties the PostHog identity to the signed-in account, and drops it on logout.
 *
 * The effect depends on `user?.id`, never on the user object. `AuthProvider`
 * also sets the user on session probe and on profile updates, so depending on
 * the object would re-`identify` on every one of those — the ref keeps the
 * latest properties available without widening the dependency.
 */
export function PostHogIdentify() {
  const { user } = useAuthContext();
  const userRef = useRef(user);
  userRef.current = user;

  const identifiedId = useRef<string | null>(null);
  const userId = user?.id;

  useEffect(() => {
    if (!isPostHogEnabled()) return;

    if (userId) {
      if (identifiedId.current === userId) return;
      identifiedId.current = userId;
      const current = userRef.current;
      identify(userId, {
        email: current?.email,
        name: current?.name,
        user_group: current?.userGroup,
        locale: current?.language,
        confirmed: current?.confirmed,
        two_factor_enabled: current?.twoFactorEnabled,
      });
      return;
    }

    // Only reset on an actual sign-out — not on the initial anonymous render,
    // which would wipe the anonymous distinct id on every page load.
    if (identifiedId.current !== null) {
      identifiedId.current = null;
      reset();
    }
  }, [userId]);

  return null;
}
